import { generatePath } from '@/services/router/generate-path';
import { workspaceRootUrl } from '@/utils/navigation';
import { isEmptyString } from '@/utils/string';
import { LOG_TYPE } from '../activity-log-types';
import type { ActivityLog, ActivityLogSource } from '../types';

/** The composer section that maps a webhook response onto profile attributes (Ember `ComposerSection`). */
const RESPONSE_MAPPINGS_TAB = 'response';

export function campaignHref(workspaceId: string | number, campaignId: number | null | undefined): string {
  return generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId', {
    workspaceId,
    campaignId: campaignId ?? '',
  });
}

/** Whether the source names an environment other than the one being viewed (Ember `isSourceFromDifferentEnvironment`). */
export function isFromOtherWorkspace(source: ActivityLogSource, workspaceId: string): boolean {
  return source.environment_id != null && source.environment_id !== Number(workspaceId);
}

/**
 * Where a source from another workspace links: the action's composer, else the automation, else
 * the workspace landing page (Ember `differentEnvironmentLinkUrl`).
 */
export function otherWorkspaceHref(source: ActivityLogSource, environmentId: number): string {
  if (source.campaign_id == null) return workspaceRootUrl(environmentId);
  if (source.action_id != null) return composerHref(environmentId, source.action_id);
  return campaignHref(environmentId, source.campaign_id);
}

function composerHref(workspaceId: number | string, actionId: number, query?: { tab: string }): string {
  return generatePath('/workspaces/:workspaceId/journeys/composer/actions/:actionId', { workspaceId, actionId }, query);
}

/**
 * The action's composer. A webhook action behind an attribute change opens on its response mappings
 * tab (Ember `actionLinkQuery`).
 */
export function actionComposerHref(log: ActivityLog, source: ActivityLogSource, workspaceId: string): string | null {
  if (source.action_id == null) return null;
  const opensResponseTab =
    source.action_type === 'webhook_action' && source.campaign_id != null && log.type === LOG_TYPE.AttributeChange;
  return composerHref(workspaceId, source.action_id, opensResponseTab ? { tab: RESPONSE_MAPPINGS_TAB } : undefined);
}

/** An LLM action opens in its automation's workflow, on that action. */
export function llmActionHref(source: ActivityLogSource, workspaceId: string): string | null {
  if (source.campaign_id == null || source.action_id == null) return null;
  return generatePath(
    '/workspaces/:workspaceId/journeys/campaigns/:campaignId/workflow',
    { workspaceId, campaignId: source.campaign_id },
    { actionId: source.action_id },
  );
}

/**
 * The journey the action ran in, when the source identifies one (Ember `journeyLinkUrl`). A
 * webhook-triggered automation has no person, so its journey opens from the automation overview.
 */
export function journeyHref(source: ActivityLogSource, workspaceId: string): string | null {
  if (isEmptyString(source.subject_id ?? '')) return null;
  const subjectId = encodeURIComponent(source.subject_id ?? '');
  if (source.campaign_type === 'webhook') {
    if (source.campaign_id == null) return null;
    return generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/journeys/:subjectId', {
      workspaceId,
      campaignId: source.campaign_id,
      subjectId,
    });
  }
  if (source.customer_id == null) return null;
  return generatePath('/workspaces/:workspaceId/people/:personId/journeys/:subjectId', {
    workspaceId,
    personId: encodeURIComponent(source.customer_id),
    subjectId,
  });
}

/**
 * The action type Ember's `actionInfo` looks up. The backend once sent `Twilio` for `twilio_action`
 * and `QueryCollection` for `collection_query_action`, so the value is underscored and suffixed.
 */
export function normalizedActionType(actionType: string): string {
  if (actionType === 'QueryCollection') return 'collection_query_action';
  const underscored = actionType
    .replace(/([a-z\d])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();
  return underscored.includes('_action') ? underscored : `${underscored}_action`;
}
